/**
 * 音声チューニングナレッジの「状態」定義 (正源)。
 *
 * ## なぜ状態を持たせるか
 *
 * Readme を **構文辞典ではなく「音声チューニングナレッジベース」** として育てるため。
 * 現場の困りごとは「解決できたもの」だけでは終わらない。
 *
 *   - 直せたもの        … 同じ相談が来たら即答できる (再調査が不要)
 *   - 改善できたが不確実 … 期待値を添えて渡す必要がある (「これで直る」と言えない)
 *   - 直せていないもの   … **残すこと自体に価値がある**
 *
 * 未解決を書き残さないと、同じ課題に別の人が同じ調査を繰り返す。
 * 「調べたが原因が分からなかった」という記録は、次の調査の出発点になる。
 * よって状態は 3 値で持ち、解決していないナレッジも一覧に並べる。
 *
 * ## 3 値の使い分け
 *
 * | 状態        | 意味                                       | 例                     |
 * | ----------- | ------------------------------------------ | ---------------------- |
 * | `solved`    | SSML で解決でき、再現性がある               | 数字の読み (say-as)    |
 * | `mitigated` | 改善方法はあるが**確実ではない**            | 方面アクセント         |
 * | `unsolved`  | 有効な手段が見つかっていない / 原因不明      | 駅名イントネーション   |
 *
 * ⚠️ 迷ったら **下げる** (`solved` にしない)。
 *   「解決済み」と書いたものが直らないと、ナレッジ全体が信用されなくなる。
 *   `solved` は「別の語・別の放送でも同じ手で直った」ものに限る。
 *
 * ## 将来の YAML 化
 *
 * 本ファイルは定数配列と型注釈のみで構成し、関数は「引く」処理しか持たない。
 * YAML 化する場合は `statuses:` セクションへそのまま移送できる。
 */

/**
 * ナレッジの状態。
 *
 *   - `solved`    … SSML で解決できた (再現性あり)
 *   - `mitigated` … 改善方法はあるが確実ではない (期待値を添えて使う)
 *   - `unsolved`  … 有効な手段が未特定 / 根本原因不明
 */
export type SsmlKnowledgeStatus = 'solved' | 'mitigated' | 'unsolved';

/** 状態 1 件の表示情報 */
export interface SsmlKnowledgeStatusDefinition {
  /** 状態キー (ナレッジデータが参照する識別子) */
  status: SsmlKnowledgeStatus;
  /**
   * 記号 (一覧を流し読みしたときに状態が目に入るようにする)。
   *
   * ⚠️ 色だけでは区別できない利用者がいるため、**記号と文言を必ず併記**する
   *    (記号単独 / 色単独では状態を伝えない)。
   */
  icon: string;
  /** 画面に出す文言 (例: 「解決済み」) */
  label: string;
  /** 状態の意味 (どういうときにこの状態を付けるか) */
  description: string;
}

/**
 * 状態定義の一覧 (配列順が画面の表示順・凡例の並び順)。
 *
 * 並びは `solved` → `mitigated` → `unsolved`。
 * 解決済みを先に置くことで「使える手が既にあるか」を先に確認できる。
 */
export const SSML_KNOWLEDGE_STATUSES: readonly SsmlKnowledgeStatusDefinition[] = [
  {
    status: 'solved',
    icon: '\u2705',
    label: '解決済み',
    description: 'SSML で解決でき、別の語・別の放送でも同じ手で直ったもの',
  },
  {
    status: 'mitigated',
    icon: '\ud83d\udfe1',
    label: '改善方法あり',
    description: '改善する手はあるが確実ではないもの (語や話者更新で結果が変わりうる)',
  },
  {
    status: 'unsolved',
    icon: '\u26aa',
    label: '未解決',
    description: '有効な手段が見つかっていない / 根本原因が分かっていないもの',
  },
];

/**
 * 状態キーから定義を引く。
 *
 * 未知のキーでは `undefined` を返す (呼び出し側でフォールバックできるようにし、
 * データ追加時のタイプミスで画面が落ちないようにする)。
 */
export function findSsmlKnowledgeStatus(status: string): SsmlKnowledgeStatusDefinition | undefined {
  return SSML_KNOWLEDGE_STATUSES.find((definition) => definition.status === status);
}

/**
 * 状態キーから画面表示用の文字列 (記号 + 文言) を作る。
 *
 * 記号と文言を分けて描画したい場合は `findSsmlKnowledgeStatus` を使う。
 * 未知のキーはキーをそのまま返し、描画を落とさない。
 */
export function ssmlKnowledgeStatusLabel(status: string): string {
  const definition = findSsmlKnowledgeStatus(status);
  return definition ? `${definition.icon} ${definition.label}` : status;
}
